import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { Suspense } from 'react';
import { multiFactor } from 'firebase/auth';
import { z } from 'zod';
import { AppShell } from '@/components/app-shell';
import { auth } from '@/lib/firebase';
import { lazyWithReload } from '@/lib/lazy-with-reload';
import { readActiveOrgId, writeActiveOrgId } from '@/lib/active-org';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/orgs-client';
import { SearchPageSearchSchema, type OrgRole, type PlatformRole } from '@leanopx/shared';

// ── Page lazy imports ──────────────────────────────────────────────────────

// Auth surfaces
const SignInPage = lazyWithReload(() =>
  import('@/pages/sign-in').then((m) => ({ default: m.SignInPage })),
);
const SignUpPage = lazyWithReload(() =>
  import('@/pages/sign-up').then((m) => ({ default: m.SignUpPage })),
);
const ForgotPasswordPage = lazyWithReload(() =>
  import('@/pages/forgot-password').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazyWithReload(() =>
  import('@/pages/reset-password').then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const VerifyEmailPage = lazyWithReload(() =>
  import('@/pages/verify-email').then((m) => ({ default: m.VerifyEmailPage })),
);
const VerifyEmailSentPage = lazyWithReload(() =>
  import('@/pages/verify-email-sent').then((m) => ({
    default: m.VerifyEmailSentPage,
  })),
);
const MfaEnrollPage = lazyWithReload(() =>
  import('@/pages/mfa-enroll').then((m) => ({ default: m.MfaEnrollPage })),
);
const MfaChallengePage = lazyWithReload(() =>
  import('@/pages/mfa-challenge').then((m) => ({
    default: m.MfaChallengePage,
  })),
);

// Onboarding + invite
const InviteAcceptPage = lazyWithReload(() =>
  import('@/pages/invite-accept').then((m) => ({ default: m.InviteAcceptPage })),
);
const OnboardingSelectOrgPage = lazyWithReload(() =>
  import('@/pages/onboarding-select-org').then((m) => ({
    default: m.OnboardingSelectOrgPage,
  })),
);
const OnboardingNoOrgPage = lazyWithReload(() =>
  import('@/pages/onboarding-no-org').then((m) => ({
    default: m.OnboardingNoOrgPage,
  })),
);
const OnboardingCreateOrgPage = lazyWithReload(() =>
  import('@/pages/onboarding-create-org').then((m) => ({
    default: m.OnboardingCreateOrgPage,
  })),
);
const OnboardingWizardPage = lazyWithReload(() =>
  import('@/pages/onboarding-wizard').then((m) => ({
    default: m.OnboardingWizardPage,
  })),
);
const OnboardingAgentLedPage = lazyWithReload(() =>
  import('@/pages/onboarding-agent-led').then((m) => ({
    default: m.OnboardingAgentLedPage,
  })),
);
const OnboardingWelcomePage = lazyWithReload(() =>
  import('@/pages/onboarding-welcome').then((m) => ({
    default: m.OnboardingWelcomePage,
  })),
);
const OnboardingWalkthroughPage = lazyWithReload(() =>
  import('@/pages/onboarding-walkthrough').then((m) => ({
    default: m.OnboardingWalkthroughPage,
  })),
);
const SetupChecklistPage = lazyWithReload(() =>
  import('@/pages/setup-checklist').then((m) => ({
    default: m.SetupChecklistPage,
  })),
);
const FirstBriefingReadyPage = lazyWithReload(() =>
  import('@/pages/first-briefing-ready').then((m) => ({
    default: m.FirstBriefingReadyPage,
  })),
);

// Brief-as-Dossier (issue #166 PR B). `/briefs` is the archive list;
// `/briefs/$briefId` is the brief reading mode with its 7-tab dossier
// surface. The detail-tab page components are wired up in the
// `briefDetail*Route` block further down this file.
const BriefsArchivePage = lazyWithReload(() =>
  import('@/pages/briefs').then((m) => ({ default: m.BriefsArchivePage })),
);

// Authed shell pages
// `/` redirects to `/today` — see `rootRedirectRoute` below. `/today`
// remains the canonical morning-briefing path; `/briefing` (added in
// NEW-2 #73) is a permanent alias that renders the same component so
// the redesign-v4 vocabulary works alongside muscle-memory and existing
// bookmarks. The legacy `CommandCenterPage` stub was dropped
// post-portal-redesign.

// Portal redesign — PR1 stubs (PR2+ will fill these in per
// docs/portal-redesign-plan.md §4)
const TodayPage = lazyWithReload(() =>
  import('@/pages/today').then((m) => ({ default: m.TodayPage })),
);
const AskPage = lazyWithReload(() => import('@/pages/ask').then((m) => ({ default: m.AskPage })));
const DossiersPage = lazyWithReload(() =>
  import('@/pages/dossiers').then((m) => ({ default: m.DossiersPage })),
);
const InsightsPage = lazyWithReload(() =>
  import('@/pages/insights').then((m) => ({ default: m.InsightsPage })),
);
const SignalsPage = lazyWithReload(() =>
  import('@/pages/signals').then((m) => ({ default: m.SignalsPage })),
);
const ExternalSignalsPage = lazyWithReload(() =>
  import('@/pages/external-signals').then((m) => ({
    default: m.ExternalSignalsPage,
  })),
);
const AlertsPage = lazyWithReload(() =>
  import('@/pages/alerts').then((m) => ({ default: m.AlertsPage })),
);
const ApprovalsPage = lazyWithReload(() =>
  import('@/pages/approvals').then((m) => ({ default: m.ApprovalsPage })),
);
const NotificationsPage = lazyWithReload(() =>
  import('@/pages/notifications').then((m) => ({ default: m.NotificationsPage })),
);
const AgentRehearsalPage = lazyWithReload(() =>
  import('@/pages/agent-rehearsal').then((m) => ({ default: m.AgentRehearsalPage })),
);
// Agent trust loop (#166 PR A) — questions queue, calibration board,
// changelog, corrections drawer host page.
const AgentQuestionsPage = lazyWithReload(() =>
  import('@/pages/agent-questions').then((m) => ({ default: m.AgentQuestionsPage })),
);
const AgentCalibrationPage = lazyWithReload(() =>
  import('@/pages/agent-calibration').then((m) => ({ default: m.AgentCalibrationPage })),
);
const AgentChangelogPage = lazyWithReload(() =>
  import('@/pages/agent-changelog').then((m) => ({ default: m.AgentChangelogPage })),
);
const AgentCorrectionsPage = lazyWithReload(() =>
  import('@/pages/agent-corrections').then((m) => ({ default: m.AgentCorrectionsPage })),
);
// `/agent/flight-plan` — issue #173 PR-B Surface 3. 3-tab "what the
// agent is planning" stub (This week / Next week / Approved). Distinct
// from `/approvals/flight-plan` (the 5-day approvals grid).
const AgentFlightPlanPage = lazyWithReload(() =>
  import('@/pages/agent-flight-plan').then((m) => ({ default: m.AgentFlightPlanPage })),
);
// Sources weight tuning (#166 PR A) — hosts <SourceWeightTuning>.
const SourcesWeightsPage = lazyWithReload(() =>
  import('@/pages/sources-weights').then((m) => ({ default: m.SourcesWeightsPage })),
);
// `/settings/cadence` — vacation toggle, quiet hours, emergency-override
// categories (#166 PR B Phase 6). The page module shipped in dd50c65 but
// its route definition got dropped during the rebase in 6fda196 — leaving
// SettingsCadencePage orphaned. Re-registered as part of the
// time-cadence-vendor-themes design-pass.
const SettingsCadencePage = lazyWithReload(() =>
  import('@/pages/settings-cadence').then((m) => ({ default: m.SettingsCadencePage })),
);
const DossierChatPage = lazyWithReload(() =>
  import('@/pages/dossier-chat').then((m) => ({ default: m.DossierChatPage })),
);
const SourcesTrustPage = lazyWithReload(() =>
  import('@/pages/sources-trust').then((m) => ({ default: m.SourcesTrustPage })),
);
const AdminAuditDiffPage = lazyWithReload(() =>
  import('@/pages/admin-audit-diff').then((m) => ({ default: m.AdminAuditDiffPage })),
);
const MetricDrillPage = lazyWithReload(() =>
  import('@/pages/metric-drill').then((m) => ({ default: m.MetricDrillPage })),
);
const MobileCompanionPage = lazyWithReload(() =>
  import('@/pages/mobile-companion').then((m) => ({ default: m.MobileCompanionPage })),
);
const GovernancePage = lazyWithReload(() =>
  import('@/pages/governance').then((m) => ({ default: m.GovernancePage })),
);
const GovernanceItemPage = lazyWithReload(() =>
  import('@/pages/governance-item').then((m) => ({ default: m.GovernanceItemPage })),
);
const GovernanceBoardPackagePage = lazyWithReload(() =>
  import('@/pages/governance-board-package').then((m) => ({
    default: m.GovernanceBoardPackagePage,
  })),
);
const GovernanceHelpPage = lazyWithReload(() =>
  import('@/pages/governance-help').then((m) => ({ default: m.GovernanceHelpPage })),
);
const GovernanceCalendarPage = lazyWithReload(() =>
  import('@/pages/governance-calendar').then((m) => ({ default: m.GovernanceCalendarPage })),
);
const OrgChartPage = lazyWithReload(() =>
  import('@/pages/org-chart').then((m) => ({ default: m.OrgChartPage })),
);
const InsightDossierPage = lazyWithReload(() =>
  import('@/pages/insight-dossier').then((m) => ({ default: m.InsightDossierPage })),
);
const StatesShowcasePage = lazyWithReload(() =>
  import('@/pages/states-showcase').then((m) => ({ default: m.StatesShowcasePage })),
);
const WalkthroughPage = lazyWithReload(() =>
  import('@/pages/walkthrough').then((m) => ({ default: m.WalkthroughPage })),
);
const OutboundPreviewPage = lazyWithReload(() =>
  import('@/pages/outbound-preview').then((m) => ({ default: m.OutboundPreviewPage })),
);
const AgentPage = lazyWithReload(() =>
  import('@/pages/agent').then((m) => ({ default: m.AgentPage })),
);
const SourcesPage = lazyWithReload(() =>
  import('@/pages/sources').then((m) => ({ default: m.SourcesPage })),
);
const BookmarksPage = lazyWithReload(() =>
  import('@/pages/bookmarks').then((m) => ({ default: m.BookmarksPage })),
);
const TodosPage = lazyWithReload(() =>
  import('@/pages/todos').then((m) => ({ default: m.TodosPage })),
);
const DiscoveryPage = lazyWithReload(() =>
  import('@/pages/discovery').then((m) => ({ default: m.DiscoveryPage })),
);
const AdminPeoplePage = lazyWithReload(() =>
  import('@/pages/admin-people').then((m) => ({ default: m.AdminPeoplePage })),
);
const AdminAuditPage = lazyWithReload(() =>
  import('@/pages/admin-audit').then((m) => ({ default: m.AdminAuditPage })),
);
const AdminEntityResolutionPage = lazyWithReload(() =>
  import('@/pages/admin-entity-resolution').then((m) => ({
    default: m.AdminEntityResolutionPage,
  })),
);
const AdminConnectorsPage = lazyWithReload(() =>
  import('@/pages/admin-connectors').then((m) => ({
    default: m.AdminConnectorsPage,
  })),
);

// v6 Integrations directory (issue #114) — 6 new orgAdmin routes for
// the marketplace gallery + Google Workspace install / configure /
// health / audit / permissions surfaces.
const IntegrationsDirectoryPage = lazyWithReload(() =>
  import('@/pages/admin-integrations').then((m) => ({
    default: m.IntegrationsDirectoryPage,
  })),
);
const GoogleWorkspaceConnectPage = lazyWithReload(() =>
  import('@/pages/admin-integrations-google-workspace').then((m) => ({
    default: m.GoogleWorkspaceConnectPage,
  })),
);
const GoogleWorkspaceConfigurePage = lazyWithReload(() =>
  import('@/pages/admin-integrations-google-workspace-configure').then((m) => ({
    default: m.GoogleWorkspaceConfigurePage,
  })),
);
const GoogleWorkspaceHealthPage = lazyWithReload(() =>
  import('@/pages/admin-integrations-google-workspace-health').then((m) => ({
    default: m.GoogleWorkspaceHealthPage,
  })),
);
const IntegrationAuditLogPage = lazyWithReload(() =>
  import('@/pages/admin-integrations-audit').then((m) => ({
    default: m.IntegrationAuditLogPage,
  })),
);
const IntegrationPermissionsPage = lazyWithReload(() =>
  import('@/pages/admin-integrations-permissions').then((m) => ({
    default: m.IntegrationPermissionsPage,
  })),
);

// Playbook (company-level + per-department) — narrative AI-editable
// operating documents. See docs/portal-redesign-plan.md §3.6.
const PlaybookPage = lazyWithReload(() =>
  import('@/pages/playbook').then((m) => ({ default: m.PlaybookPage })),
);
const DepartmentPlaybookPage = lazyWithReload(() =>
  import('@/pages/department-playbook').then((m) => ({
    default: m.DepartmentPlaybookPage,
  })),
);

// Notes — multi-media personal notes (markdown body + images + files +
// voice memos with AI-generated transcripts).
const NotesPage = lazyWithReload(() =>
  import('@/pages/notes').then((m) => ({ default: m.NotesPage })),
);

// Dossier detail pages — entity profiles for People + Topics today.
// Customers + Products empty-state until connectors land (Phase 3).
const DossierPersonPage = lazyWithReload(() =>
  import('@/pages/dossier-person').then((m) => ({
    default: m.DossierPersonPage,
  })),
);
const DossierTopicPage = lazyWithReload(() =>
  import('@/pages/dossier-topic').then((m) => ({
    default: m.DossierTopicPage,
  })),
);

// EOS surfaces still in scope after the May 2026 pivot. /vto + /people-analyzer
// were deleted in issue #143 — they had no design across two consecutive
// handoff bundles and the operating-loop surfaces (issues/rocks/scorecard/
// playbook) cover the EOS framework needs.
const RocksPage = lazyWithReload(() =>
  import('@/pages/rocks').then((m) => ({ default: m.RocksPage })),
);
const ScorecardPage = lazyWithReload(() =>
  import('@/pages/scorecard').then((m) => ({ default: m.ScorecardPage })),
);
const IssuesPage = lazyWithReload(() =>
  import('@/pages/issues').then((m) => ({ default: m.IssuesPage })),
);
const L10Page = lazyWithReload(() => import('@/pages/l10').then((m) => ({ default: m.L10Page })));
const AccountabilityPage = lazyWithReload(() =>
  import('@/pages/accountability').then((m) => ({
    default: m.AccountabilityPage,
  })),
);

// v7 Phase 5 — Rules & automations surface (#123). orgAdmin-only;
// sidebar entry lives under "Trust" alongside Audit log + Integrations.
const RulesPage = lazyWithReload(() =>
  import('@/pages/rules').then((m) => ({ default: m.RulesPage })),
);

// Org admin surfaces
const AdminSettingsPage = lazyWithReload(() =>
  import('@/pages/admin-settings').then((m) => ({
    default: m.AdminSettingsPage,
  })),
);
const OrgSettingsPage = lazyWithReload(() =>
  import('@/pages/org-settings').then((m) => ({ default: m.OrgSettingsPage })),
);
// Legacy split surfaces — the /settings/org/members + /settings/org/
// invites URLs still resolve, but they now render the unified
// `<OrgTeamPage>` (v6 dev-handoff #114) so deep links and bookmarks
// don't break. Sidebar + UI link to `/settings/org/team`.
const OrgTeamPage = lazyWithReload(() =>
  import('@/pages/org-team').then((m) => ({ default: m.OrgTeamPage })),
);

// v6 dev-handoff (#114): Billing, Profile, Empty workspace, 404/403.
const BillingPage = lazyWithReload(() =>
  import('@/pages/billing').then((m) => ({ default: m.BillingPage })),
);
const ProfilePage = lazyWithReload(() =>
  import('@/pages/profile').then((m) => ({ default: m.ProfilePage })),
);
const CompanyProfilePage = lazyWithReload(() =>
  import('@/pages/profile-company').then((m) => ({ default: m.CompanyProfilePage })),
);
const EmptyWorkspacePage = lazyWithReload(() =>
  import('@/pages/empty-workspace').then((m) => ({
    default: m.EmptyWorkspacePage,
  })),
);
const NotFoundPage = lazyWithReload(() =>
  import('@/pages/not-found').then((m) => ({ default: m.NotFoundPage })),
);
const PermissionDeniedPage = lazyWithReload(() =>
  import('@/pages/not-found').then((m) => ({ default: m.PermissionDeniedPage })),
);

// v7 dev-handoff (Epic #123): read-only RBAC matrix at `/permissions`.
// Trust-section sidebar entry, admin-only at both the route guard and
// the sidebar render layer.
const PermissionsPage = lazyWithReload(() =>
  import('@/pages/permissions').then((m) => ({ default: m.PermissionsPage })),
);

// Design-pass wave (May 2026): 8 new surfaces — agent-transparency
// (memory feed + near-misses), the decisions ledger, an alternate
// chronological notes view, global search, the Monday-morning
// overnight retro, the flight-plan approvals view, plus the
// post-onboarding "reveal" page that sits between the wizard and
// /today.
const MemoryPage = lazyWithReload(() =>
  import('@/pages/memory').then((m) => ({ default: m.MemoryPage })),
);
const DecisionsPage = lazyWithReload(() =>
  import('@/pages/decisions').then((m) => ({ default: m.DecisionsPage })),
);
const NotesTimelinePage = lazyWithReload(() =>
  import('@/pages/notes-timeline').then((m) => ({ default: m.NotesTimelinePage })),
);
const SearchPage = lazyWithReload(() =>
  import('@/pages/search').then((m) => ({ default: m.SearchPage })),
);
const BriefingOvernightPage = lazyWithReload(() =>
  import('@/pages/briefing-overnight').then((m) => ({ default: m.BriefingOvernightPage })),
);
// Brief-as-Dossier detail surfaces (#166 PR B) — 7 sibling routes under
// `/briefs/$briefId/*`. The default (Items) lives at `/briefs/$briefId`;
// the six other tabs are sibling URLs that re-render the same chrome
// (`<BriefDetailShell>`) with a different tab active.
const BriefDetailItemsPage = lazyWithReload(() =>
  import('@/pages/briefs.$briefId').then((m) => ({ default: m.BriefDetailItemsPage })),
);
const BriefDetailActionLedgerPage = lazyWithReload(() =>
  import('@/pages/briefs.$briefId.action-ledger').then((m) => ({
    default: m.BriefDetailActionLedgerPage,
  })),
);
const BriefDetailNotesPage = lazyWithReload(() =>
  import('@/pages/briefs.$briefId.notes').then((m) => ({ default: m.BriefDetailNotesPage })),
);
const BriefDetailCitationsPage = lazyWithReload(() =>
  import('@/pages/briefs.$briefId.citations').then((m) => ({
    default: m.BriefDetailCitationsPage,
  })),
);
const BriefDetailOutcomesPage = lazyWithReload(() =>
  import('@/pages/briefs.$briefId.outcomes').then((m) => ({
    default: m.BriefDetailOutcomesPage,
  })),
);
const BriefDetailSourceSnapshotPage = lazyWithReload(() =>
  import('@/pages/briefs.$briefId.source-snapshot').then((m) => ({
    default: m.BriefDetailSourceSnapshotPage,
  })),
);
const BriefDetailSharePage = lazyWithReload(() =>
  import('@/pages/briefs.$briefId.share').then((m) => ({ default: m.BriefDetailSharePage })),
);
const FlightPlanPage = lazyWithReload(() =>
  import('@/pages/flight-plan').then((m) => ({ default: m.FlightPlanPage })),
);
// `/agent/slack-preview` — issue #173 PR-B Surface 8 (SlackNative).
const AgentSlackPreviewPage = lazyWithReload(() =>
  import('@/pages/agent-slack-preview').then((m) => ({ default: m.AgentSlackPreviewPage })),
);
// `/settings/agent` — issue #180 / #173 PR-B Surface 9 (MetaPersonalization).
// Dedicated Agent Persona + Meta-personalization page; admin-gated at the
// route layer (matches `/admin/settings` since it edits org-wide
// `AgentSettings` via the same `api.putAgentSettings` write path).
const SettingsAgentPage = lazyWithReload(() =>
  import('@/pages/settings-agent').then((m) => ({ default: m.SettingsAgentPage })),
);
const NearMissesPage = lazyWithReload(() =>
  import('@/pages/near-misses').then((m) => ({ default: m.NearMissesPage })),
);
const OnboardingRevealPage = lazyWithReload(() =>
  import('@/pages/onboarding-reveal').then((m) => ({ default: m.OnboardingRevealPage })),
);

/**
 * `/onboarding/ingestion` + `/onboarding/invite-team` — the post-wizard
 * "row-03" flow: read your stack → invite the team → land on the reveal.
 * Both full-bleed (mounted at `rootRoute`, not `authedRoute`) so the
 * dark-canvas hero matches the rest of the onboarding rail.
 */
const OnboardingIngestionPage = lazyWithReload(() =>
  import('@/pages/onboarding-ingestion').then((m) => ({ default: m.OnboardingIngestionPage })),
);
const OnboardingInviteTeamPage = lazyWithReload(() =>
  import('@/pages/onboarding-invite-team').then((m) => ({
    default: m.OnboardingInviteTeamPage,
  })),
);

// SuperAdmin surfaces
const SuperAdminPage = lazyWithReload(() =>
  import('@/pages/superadmin').then((m) => ({ default: m.SuperAdminPage })),
);
const SuperAdminOrgsNewPage = lazyWithReload(() =>
  import('@/pages/superadmin-orgs-new').then((m) => ({
    default: m.SuperAdminOrgsNewPage,
  })),
);
const SuperAdminUsersPage = lazyWithReload(() =>
  import('@/pages/superadmin-users').then((m) => ({
    default: m.SuperAdminUsersPage,
  })),
);
const SuperAdminAuditPage = lazyWithReload(() =>
  import('@/pages/superadmin-audit').then((m) => ({
    default: m.SuperAdminAuditPage,
  })),
);

// ── Root ───────────────────────────────────────────────────────────────────

const rootRoute = createRootRoute({
  component: () => (
    <Suspense fallback={null}>
      <Outlet />
    </Suspense>
  ),
});

// ── Auth helpers ───────────────────────────────────────────────────────────

function readClaims(claims: Record<string, unknown>): {
  platformRole: PlatformRole | null;
  orgsClaim: Record<string, OrgRole>;
} {
  const rawPlatform = claims['platformRole'];
  const platformRole: PlatformRole | null = rawPlatform === 'superAdmin' ? 'superAdmin' : null;
  const rawOrgs = claims['orgs'];
  const orgsClaim: Record<string, OrgRole> = {};
  if (rawOrgs && typeof rawOrgs === 'object') {
    for (const [id, role] of Object.entries(rawOrgs as Record<string, unknown>)) {
      if (role === 'admin' || role === 'member') orgsClaim[id] = role;
    }
  }
  return { platformRole, orgsClaim };
}

/** Resolve onboarding path from the persisted `onboardingVariant` pref.
 *  v6 (issue #114) — default flips back to the agent-led 5-step
 *  conversational interview (now persists via the same seed pipeline
 *  as the form-first 8-step wizard). The form wizard stays available
 *  for users who set `onboardingVariant: 'form-first'`. */
function readOnboardingTarget(): '/onboarding/wizard' | '/onboarding/agent-led' {
  try {
    if (typeof localStorage === 'undefined') return '/onboarding/agent-led';
    const raw = localStorage.getItem('leanopx-ui-prefs');
    if (!raw) return '/onboarding/agent-led';
    const parsed = JSON.parse(raw) as { state?: { onboardingVariant?: string } };
    const variant = parsed?.state?.onboardingVariant;
    if (variant === 'form-first') return '/onboarding/wizard';
    return '/onboarding/agent-led';
  } catch {
    return '/onboarding/agent-led';
  }
}

/**
 * Auth guard for routes that require a signed-in user with an active
 * org. `allowNoOrg` opt-out is used by `/onboarding/create-org` — the
 * self-serve org-create flow needs an authed user but the user is
 * deliberately membership-less at that point, so the standard
 * "no membership → redirect to create-org" branch would infinite-loop.
 * When `allowNoOrg` is true we keep every other check (email verify,
 * MFA-when-has-membership, stored-org validation) but skip the
 * membership redirect.
 */
/**
 * Returns the parsed claims so downstream beforeLoads can reuse them
 * instead of re-issuing `getIdTokenResult()` (the SDK caches, but DRY
 * also makes the data flow obvious). `null` when `allowNoOrg: true` lets
 * a membership-less user through — those callers don't have claims to act
 * on anyway.
 */
async function requireAuth(
  opts: { allowNoOrg?: boolean } = {},
): Promise<{
  orgsClaim: ReturnType<typeof readClaims>['orgsClaim'];
  platformRole: ReturnType<typeof readClaims>['platformRole'];
} | null> {
  const { allowNoOrg = false } = opts;
  await auth.authStateReady();
  const current = auth.currentUser;
  if (!current) throw redirect({ to: '/sign-in' });

  const isPasswordProvider = current.providerData.some((p) => p.providerId === 'password');
  if (isPasswordProvider && !current.emailVerified) {
    throw redirect({ to: '/verify-email-sent' });
  }

  // `DEV` guard matches firebase.ts — see the comment there for the
  // precise semantics (`DEV` is `mode !== 'production'`, not strictly
  // `mode === 'development'`). The MFA enrollment gate below uses this
  // flag as a bypass for the seeded test personas; allowing the bypass
  // to flip true in prod would let real users skip enrollment, so the
  // `DEV` check here is a security boundary, not just a UX one.
  const inEmulator = import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';
  const tokenResult = await current.getIdTokenResult();
  const { orgsClaim, platformRole } = readClaims(tokenResult.claims);
  const hasAnyMembership = Object.keys(orgsClaim).length > 0;

  // Test personas (seeded via scripts/seed-test-personas.mjs) carry a
  // `testPersona: true` custom claim and skip the MFA gate. The claim is
  // server-set (admin SDK only); Firestore rules + API guards ignore MFA
  // entirely, so this is purely a client-side UX bypass — never a
  // security boundary.
  const isTestPersona = tokenResult.claims['testPersona'] === true;

  if (!inEmulator && !isTestPersona && hasAnyMembership) {
    const enrolled = multiFactor(current).enrolledFactors.length > 0;
    if (!enrolled) throw redirect({ to: '/mfa-enroll' });
  }

  if (!hasAnyMembership) {
    if (allowNoOrg) return null;
    throw redirect({ to: '/onboarding/create-org' });
  }
  const stored = readActiveOrgId();
  const storedIsValid = stored != null && orgsClaim[stored] !== undefined;
  if (!storedIsValid) {
    const ids = Object.keys(orgsClaim);
    if (ids.length === 1 && ids[0]) {
      writeActiveOrgId(ids[0]);
    } else {
      throw redirect({ to: '/onboarding/select-org' });
    }
  }
  return { orgsClaim, platformRole };
}

async function requireSuperAdmin() {
  await auth.authStateReady();
  const current = auth.currentUser;
  if (!current) throw redirect({ to: '/sign-in' });
  const tokenResult = await current.getIdTokenResult();
  const { platformRole } = readClaims(tokenResult.claims);
  if (platformRole !== 'superAdmin') throw redirect({ to: '/' });
}

async function requireOrgAdmin() {
  await auth.authStateReady();
  const current = auth.currentUser;
  if (!current) throw redirect({ to: '/sign-in' });
  const tokenResult = await current.getIdTokenResult();
  const { platformRole, orgsClaim } = readClaims(tokenResult.claims);
  if (platformRole === 'superAdmin') return;
  const stored = readActiveOrgId();
  const role = stored ? orgsClaim[stored] : undefined;
  if (role !== 'admin') throw redirect({ to: '/' });
}

async function requireMembership() {
  await auth.authStateReady();
  const current = auth.currentUser;
  if (!current) throw redirect({ to: '/sign-in' });
  const tokenResult = await current.getIdTokenResult();
  const { orgsClaim } = readClaims(tokenResult.claims);
  if (Object.keys(orgsClaim).length === 0) {
    throw redirect({ to: '/onboarding/create-org' });
  }
}

async function requireUserForEnroll() {
  await auth.authStateReady();
  if (!auth.currentUser) throw redirect({ to: '/sign-in' });
}

/**
 * Onboarding gate applied to every authed shell route. Runs AFTER
 * `requireAuth()` so the user, claims, and active org are all valid.
 *
 * Why this lives on `authedRoute` (not just `/`): the original gate at
 * `rootRedirectRoute` only fired when the URL was exactly `/`. A user
 * who bookmarked `/today` (or any other authed surface) bypassed the
 * gate entirely. The 2026-05 bulk onboarding reset surfaced this — admins
 * landed at their bookmarked /today instead of the wizard, because nothing
 * on /today's route chain checks BusinessContext.onboardingCompleted.
 *
 * Behavior:
 *   - Non-admins always fall through (they can't submit the wizard;
 *     `PUT /business-context` is orgAdmin-only). The empty-state callouts
 *     on /today / /briefing tell them to ask their admin.
 *   - Admins: fetch BusinessContext once; if !onboardingCompleted, redirect
 *     to the wizard (per `readOnboardingTarget`).
 *   - Network errors / 500s fall through to the requested route — a
 *     transient outage shouldn't trap an admin in onboarding.
 *   - The onboarding wizard routes themselves are siblings of authedRoute
 *     (mounted on rootRoute), so calling this from authedRoute.beforeLoad
 *     does NOT loop back through the gate.
 *
 * Claims are passed in from `requireAuth()` so we don't re-issue
 * `getIdTokenResult()` immediately after it already ran.
 */
async function ensureOnboardingComplete(
  claims: {
    orgsClaim: ReturnType<typeof readClaims>['orgsClaim'];
    platformRole: ReturnType<typeof readClaims>['platformRole'];
  } | null,
) {
  if (!claims) return;
  const orgId = readActiveOrgId();
  if (!orgId) return;
  const isAdmin = claims.orgsClaim[orgId] === 'admin' || claims.platformRole === 'superAdmin';
  if (!isAdmin) return;

  const onboardingPath = readOnboardingTarget();
  try {
    const ctx = await api.getBusinessContext(orgId);
    if (!ctx.onboardingCompleted) throw redirect({ to: onboardingPath });
  } catch (err) {
    if (err && typeof err === 'object' && 'isRedirect' in err) throw err;
    if (err instanceof ApiError && err.status === 404) {
      throw redirect({ to: onboardingPath });
    }
    // Transient — let the user proceed to the requested route.
  }
}

// ── Public + auth routes ───────────────────────────────────────────────────

const oobCodeSearch = z.object({ oobCode: z.string() });

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-in',
  component: SignInPage,
});
const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sign-up',
  component: SignUpPage,
});
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
  validateSearch: (search) => oobCodeSearch.parse(search),
});
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  component: VerifyEmailPage,
  validateSearch: (search) => oobCodeSearch.parse(search),
});
const verifyEmailSentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email-sent',
  component: VerifyEmailSentPage,
});
const mfaEnrollRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mfa-enroll',
  beforeLoad: requireUserForEnroll,
  component: MfaEnrollPage,
});
const mfaChallengeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mfa-challenge',
  component: MfaChallengePage,
});

const inviteAcceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invite/$token',
  component: InviteAcceptPage,
});
const onboardingSelectOrgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/select-org',
  beforeLoad: requireMembership,
  component: OnboardingSelectOrgPage,
});
/**
 * Legacy `/onboarding/no-org` dead-end. The self-serve org-create flow
 * (feat/self-serve-org-create-trial) replaced this with a real "start
 * your free trial" surface at `/onboarding/create-org`. We keep the
 * route mounted so existing invite emails, browser bookmarks, and any
 * deep links from older builds resolve to the new flow instead of
 * 404ing. The `OnboardingNoOrgPage` component is still exported for the
 * one-off rare case where a redirect-loop test or a paranoid
 * support-debug URL needs the original dead-end copy; it is no longer
 * reachable through the route tree.
 */
const onboardingNoOrgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/no-org',
  beforeLoad: () => {
    throw redirect({ to: '/onboarding/create-org' });
  },
  component: OnboardingNoOrgPage,
});
const onboardingCreateOrgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/create-org',
  // `allowNoOrg: true` skips the standard "no membership → redirect to
  // create-org" branch — otherwise this very route would bounce a fresh
  // user back to itself forever. Every other gate (email verify, MFA-
  // when-has-membership) still runs.
  beforeLoad: () => requireAuth({ allowNoOrg: true }),
  component: OnboardingCreateOrgPage,
});

/**
 * Onboarding wizard — captures BusinessContext after org creation but
 * before the user lands on /today. We re-use `requireAuth` so the same
 * org-selection / MFA / no-membership gates apply; the rootRedirect
 * route below sniffs the BusinessContext doc and redirects here when
 * `onboardingCompleted` is false. Mounted at the rootRoute level (not
 * authedRoute) so it renders full-screen without the AppShell chrome.
 *
 * Non-admin members are bounced to `/today` — the submit endpoint
 * (`PUT /business-context`) is orgAdmin-only, so letting a member
 * deep-link in would trap them with a 403 on submit. superAdmins are
 * treated as admin (same as `requireOrgAdmin` / `useIsOrgAdmin`) so
 * they don't hit a redirect trap when clicking through from `/today`'s
 * "Run wizard" CTA.
 */
const onboardingWizardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/wizard',
  beforeLoad: async () => {
    const claims = await requireAuth();
    // requireAuth() guarantees an authed user with an active org by
    // the time it returns (it redirects otherwise). The null branches
    // below are defense-in-depth — if a future requireAuth refactor
    // ever lets them through, we still bounce to /today rather than
    // silently rendering the wizard for an un-gated user. Bare
    // `return` from beforeLoad would let the route component mount.
    if (!claims) throw redirect({ to: '/today' });
    const orgId = readActiveOrgId();
    if (!orgId) throw redirect({ to: '/today' });
    const isAdmin = claims.orgsClaim[orgId] === 'admin' || claims.platformRole === 'superAdmin';
    if (!isAdmin) throw redirect({ to: '/today' });
  },
  component: OnboardingWizardPage,
});
const onboardingAgentLedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/agent-led',
  beforeLoad: async () => {
    const claims = await requireAuth();
    if (!claims) throw redirect({ to: '/today' });
    const orgId = readActiveOrgId();
    if (!orgId) throw redirect({ to: '/today' });
    const isAdmin = claims.orgsClaim[orgId] === 'admin' || claims.platformRole === 'superAdmin';
    if (!isAdmin) throw redirect({ to: '/today' });
  },
  component: OnboardingAgentLedPage,
});

/**
 * `/onboarding/welcome` — post-email-verification landing that lets
 * the user pick agent-led vs self-serve wizard. Authed but full-bleed
 * (mounted on rootRoute, not authedRoute) so the dark-canvas hero
 * renders without the AppShell chrome. `allowNoOrg: false` is implicit
 * — the user must have an active org by the time they land here
 * (org-create runs before email verification in the standard flow).
 */
const onboardingWelcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/welcome',
  beforeLoad: () => requireAuth(),
  component: OnboardingWelcomePage,
});

/**
 * `/onboarding/reveal` — the post-wizard "magic moment" page. Authed
 * but full-bleed (mounts at rootRoute, not authedRoute) so it renders
 * without the AppShell chrome — same pattern as the wizard + warming-up
 * interstitial. The agent-led wizard's completion handler now nav's
 * here instead of straight to `/today`; the page's own primary CTA
 * carries the user the rest of the way.
 */
const onboardingRevealRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/reveal',
  beforeLoad: () => requireAuth(),
  component: OnboardingRevealPage,
});

/**
 * `/onboarding/ingestion` — Step 5 of the post-wizard sequence: the
 * "Reading 90 days of your stack" interstitial. Full-bleed. Once the
 * client-side simulation finishes (or the user closes), routes to
 * `/onboarding/invite-team`.
 */
const onboardingIngestionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/ingestion',
  beforeLoad: () => requireAuth(),
  component: OnboardingIngestionPage,
});

/**
 * `/onboarding/invite-team` — optional post-wizard step that surfaces
 * detected calendar attendees + a manual-add input. Once invites are
 * sent (or skipped), routes to `/onboarding/reveal`. orgAdmin in
 * practice — the underlying POST `/api/orgs/:o/invites` endpoint is
 * orgAdmin-only, so a non-admin who lands here will see a server-side
 * 403 on send; the page itself sits behind `requireAuth` only.
 */
const onboardingInviteTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/invite-team',
  beforeLoad: () => requireAuth(),
  component: OnboardingInviteTeamPage,
});

/**
 * `/onboarding/walkthrough` — first-run forced overlay tour. Mounted
 * at rootRoute (full-bleed) so the dim wash + spotlight render on a
 * private canvas instead of the live AppShell. Today reachable via
 * direct URL only; the agent-led wizard's completion handler will
 * push the user here when the post-onboarding handoff lands.
 */
const onboardingWalkthroughRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding/walkthrough',
  beforeLoad: () => requireAuth(),
  component: OnboardingWalkthroughPage,
});

/**
 * `/first-briefing-ready` — Day 2-3 celebration when the agent has
 * finished synthesizing the user's first Monday briefing. Full-bleed
 * (rootRoute) so the confetti + push-notification chrome paint
 * edge-to-edge. Static client-side artifact today; the trigger from
 * a real backend signal is a deferred epic.
 */
const firstBriefingReadyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/first-briefing-ready',
  beforeLoad: () => requireAuth(),
  component: FirstBriefingReadyPage,
});

// ── Authed shell ───────────────────────────────────────────────────────────

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: async () => {
    const claims = await requireAuth();
    await ensureOnboardingComplete(claims);
  },
  component: AppShell,
});

/**
 * `/` lands on /today. The onboarding gate runs on the parent authedRoute
 * (see `ensureOnboardingComplete`), so by the time we reach here either
 * onboarding is done or we've already been redirected to the wizard.
 */
const rootRedirectRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/today' });
  },
  component: () => null,
});

// Portal redesign nav stubs — `/today` is the canonical morning-
// briefing path; `/briefing` (added in NEW-2 #73) is a permanent
// alias rendering the same component.
const briefingRoute = createRoute({
  // /briefing aliases /today (both URLs land on TodayPage). The v4
  // section restructure (operating-loop strip, hero insights + drafts
  // + calendar in v4 order) lands in a follow-up — until then the
  // alias keeps the redesign vocabulary live alongside the canonical
  // /today path so users can use either URL.
  getParentRoute: () => authedRoute,
  path: '/briefing',
  component: TodayPage,
});
const todayRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/today',
  component: TodayPage,
});
// Brief-as-Dossier (#166 PR B) — archive list at `/briefs`. The detail
// routes for `/briefs/$briefId` and its six sibling tabs are registered
// in the `briefDetail*Route` block below.
const briefsArchiveRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/briefs',
  beforeLoad: requireMembership,
  component: BriefsArchivePage,
});
const askSearch = z.object({
  thread: z.string().optional(),
  /** Pre-fill query — handed off from the command palette when the user
   *  typed in the search box but didn't pick a route, and hit Enter. */
  q: z.string().min(1).max(2000).optional(),
});
const askRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/ask',
  component: AskPage,
  validateSearch: (search) => askSearch.parse(search),
});
const dossiersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/dossiers',
  component: DossiersPage,
});
const insightsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/insights',
  component: InsightsPage,
});
const signalsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/signals',
  component: SignalsPage,
});
const externalSignalsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/signals/external',
  component: ExternalSignalsPage,
});
const alertsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/alerts',
  component: AlertsPage,
});
const approvalsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/approvals',
  // v7 Phase 4 (Epic #123). Reads org-scoped draft approvals; gate on
  // membership so a user with a revoked/swapped org context can't reach
  // the page (matches `/profile/company` + `/outbound/preview`).
  beforeLoad: requireMembership,
  component: ApprovalsPage,
});
const notificationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/notifications',
  component: NotificationsPage,
});
const agentRehearsalRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/agent/rehearsal',
  component: AgentRehearsalPage,
});
// Agent trust loop (#166 PR A) — four sibling surfaces under /agent.
const agentQuestionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/agent/questions',
  component: AgentQuestionsPage,
});
const agentCalibrationRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/agent/calibration',
  component: AgentCalibrationPage,
});
const agentChangelogRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/agent/changelog',
  component: AgentChangelogPage,
});
const agentCorrectionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/agent/corrections',
  component: AgentCorrectionsPage,
});
// `/agent/flight-plan` — issue #173 PR-B Surface 3 stub. UI-only for
// now; backend `agent_scheduled_actions` collection lands in a follow-up.
const agentFlightPlanRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/agent/flight-plan',
  component: AgentFlightPlanPage,
});
// `/agent/slack-preview` — issue #173 PR-B Surface 8 (SlackNative).
// Static showcase of what the LeanOpx agent looks like inside a Slack
// DM. The Slack app install + bot wiring is a separate epic; this page
// is the trust-building visual operators see before granting scopes.
const agentSlackPreviewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/agent/slack-preview',
  component: AgentSlackPreviewPage,
});
// `/settings/agent` — issue #180 (last B1 surface). Admin-only, since
// the page writes the org-wide `AgentSettings` doc via the same
// `api.putAgentSettings` write path that `/admin/settings?tab=agent`
// uses. The Suggestions tab inside is currently UI-only; backend
// signal source ships in a follow-up.
const settingsAgentRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/agent',
  beforeLoad: requireOrgAdmin,
  component: SettingsAgentPage,
});
const sourcesWeightsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/sources',
  component: SourcesWeightsPage,
});
// `/settings/cadence` — per-user vacation + quiet hours (#166 PR B Phase 6).
// Route registration restored after the 6fda196 rebase dropped it. No
// admin gate: vacation/quiet-hours preferences are per-user state on
// `userPreferences/{orgId}_{userId}_vacationMode`, not an org-level
// policy.
const settingsCadenceRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/cadence',
  component: SettingsCadencePage,
});
const dossierChatRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/dossiers/$kind/$key/chat',
  component: DossierChatPage,
});
const sourcesTrustRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/connectors/trust',
  beforeLoad: requireOrgAdmin,
  component: SourcesTrustPage,
});
const adminAuditDiffRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/audit/$id',
  beforeLoad: requireOrgAdmin,
  component: AdminAuditDiffPage,
});
const metricDrillRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/scorecard/$measurableId',
  component: MetricDrillPage,
});
const mobileCompanionRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/m',
  component: MobileCompanionPage,
});
const governanceRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/governance',
  component: GovernancePage,
});
const governanceCalendarRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/governance/calendar',
  component: GovernanceCalendarPage,
});
const governanceHelpRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/governance/help',
  component: GovernanceHelpPage,
});
const governanceBoardPackageRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/governance/board/$quarterKey',
  component: GovernanceBoardPackagePage,
});
const governanceItemRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/governance/$itemId',
  component: GovernanceItemPage,
});
const orgChartRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/accountability/chart',
  component: OrgChartPage,
});
const insightDossierRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/insights/$insightId',
  component: InsightDossierPage,
});
const statesShowcaseRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/design/states',
  // Internal design-reference page (empty / loading / error patterns).
  // Not user-facing in production — gate on superAdmin so regular org
  // members can't stumble into it.
  beforeLoad: requireSuperAdmin,
  component: StatesShowcasePage,
});
const walkthroughRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/walkthrough',
  // First-run guided tour (designer-faithful scaffold). Reachable today
  // via direct URL + ⌘K palette only — auto-launch-on-first-sign-in is
  // a follow-up product decision.
  component: WalkthroughPage,
});
const outboundPreviewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/outbound/preview',
  // Phase 7 of Epic #123 (v7 redesign). Pure-presentation preview of
  // how the day's briefing arrives via email · Slack DM · iOS push.
  // No new backend; delivery wiring is deferred (see Epic #123 open
  // questions). Reached from `/today` via the "Preview delivery" link
  // in the drafts strip; deliberately not in the sidebar.
  //
  // Gated on membership: the preview composes the day's `Briefing` via
  // `api.getBriefing(orgId, …)` plus the pinned hero insights via
  // `api.listInsights(orgId, …)` — both org-scoped endpoints. The
  // guard is the page's load-bearing access check today (not just
  // pre-wiring defense-in-depth for a future delivery backend).
  beforeLoad: requireMembership,
  component: OutboundPreviewPage,
});
const agentRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/agent',
  component: AgentPage,
});
const sourcesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sources',
  component: SourcesPage,
});
const bookmarksRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/bookmarks',
  component: BookmarksPage,
});
const todosRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/todos',
  component: TodosPage,
});
const discoveryRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/discovery',
  beforeLoad: requireOrgAdmin,
  component: DiscoveryPage,
});
const adminPeopleRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/people',
  beforeLoad: requireOrgAdmin,
  component: AdminPeoplePage,
});
const adminAuditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/audit',
  beforeLoad: requireOrgAdmin,
  component: AdminAuditPage,
});
const adminEntityResolutionRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/entity-resolution',
  beforeLoad: requireOrgAdmin,
  component: AdminEntityResolutionPage,
});
const adminConnectorsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/connectors',
  beforeLoad: requireOrgAdmin,
  component: AdminConnectorsPage,
});

// v6 Integrations directory (issue #114). The /admin/connectors page
// stays as post-install management; these routes are the new
// marketplace + per-integration surfaces.
const adminIntegrationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/integrations',
  beforeLoad: requireOrgAdmin,
  component: IntegrationsDirectoryPage,
});
const adminIntegrationsGoogleWorkspaceRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/integrations/google-workspace',
  beforeLoad: requireOrgAdmin,
  component: GoogleWorkspaceConnectPage,
});
const adminIntegrationsGoogleWorkspaceConfigureRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/integrations/google-workspace/configure',
  beforeLoad: requireOrgAdmin,
  component: GoogleWorkspaceConfigurePage,
});
const adminIntegrationsGoogleWorkspaceHealthRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/integrations/google-workspace/health',
  beforeLoad: requireOrgAdmin,
  component: GoogleWorkspaceHealthPage,
});
const adminIntegrationsAuditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/integrations/audit',
  beforeLoad: requireOrgAdmin,
  component: IntegrationAuditLogPage,
});
const adminIntegrationsPermissionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/integrations/permissions',
  beforeLoad: requireOrgAdmin,
  component: IntegrationPermissionsPage,
});

// Playbook routes — list + per-department detail.
const playbookRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/playbook',
  component: PlaybookPage,
});
const departmentPlaybookRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/playbook/$deptSlug',
  component: DepartmentPlaybookPage,
});

// Notes — `/notes` universal timeline. The `?noteId=` search param
// auto-selects a specific note on load (used by the "from note" chip
// on the /todos page so promote-from-note todos can deep-link back).
const notesSearch = z.object({ noteId: z.string().optional() });
const notesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/notes',
  component: NotesPage,
  validateSearch: (search) => notesSearch.parse(search),
});

// Dossier detail routes — `/dossiers/person/$uid`, `/dossiers/topic/$key`.
// Customers + products are list-empty-state today; their detail routes
// land alongside the connector pipeline.
const dossierPersonRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/dossiers/person/$uid',
  component: DossierPersonPage,
});
const dossierTopicRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/dossiers/topic/$topicKey',
  component: DossierTopicPage,
});

// EOS surfaces (kept as stubs while the redesign decides which fold
// into the new nav)
const rocksRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/rocks',
  component: RocksPage,
});
const scorecardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/scorecard',
  component: ScorecardPage,
});
const issuesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/issues',
  component: IssuesPage,
});
const l10Route = createRoute({
  getParentRoute: () => authedRoute,
  path: '/l10',
  component: L10Page,
});
const accountabilityRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/accountability',
  component: AccountabilityPage,
});
// v7 Phase 5 — Rules & automations (#123). orgAdmin-gated.
const rulesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/rules',
  beforeLoad: requireOrgAdmin,
  component: RulesPage,
});

// Design-pass wave (May 2026) — 7 new in-product routes.
//
// `/memory` and `/memory/near-misses` are the agent-transparency
// feeds; `/decisions` is the company-wide decisions ledger;
// `/notes/timeline` is the chronological view of personal notes;
// `/search` is the global search surface (Zod-validated `?q=` param
// matching the `notesRoute` pattern above); `/briefing/overnight` is
// the Monday-morning retro; `/approvals/flight-plan` is the
// approvals queue's "day shape" view.
const memoryRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/memory',
  component: MemoryPage,
});
const nearMissesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/memory/near-misses',
  component: NearMissesPage,
});
const decisionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/decisions',
  component: DecisionsPage,
});
const notesTimelineRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/notes/timeline',
  component: NotesTimelinePage,
});
// `/search` URL contract: `?q=…&type=…&time=…&confidence=…&source=…`. The
// schema lives in `packages/shared/src/search/filters.ts` so the router,
// the page, and any future deep-link tooling share one source of truth.
// Each facet is enum-narrowed (vs the prior loose `z.string().optional()`),
// so malformed values are rejected at the URL boundary rather than
// silently falling through to the rail's defaults.
const searchRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/search',
  validateSearch: (search) => SearchPageSearchSchema.parse(search),
  component: SearchPage,
});
const briefingOvernightRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/briefing/overnight',
  component: BriefingOvernightPage,
});
// Brief-as-Dossier detail routes (#166 PR B). The default tab (Items)
// is the bare `/briefs/$briefId` URL; the explicit `/items` variant is
// listed so the tab strip's `<Link to>` targets always match the active
// route exactly.
const briefDetailItemsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/briefs/$briefId',
  component: BriefDetailItemsPage,
});
const briefDetailItemsAliasRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/briefs/$briefId/items',
  component: BriefDetailItemsPage,
});
const briefDetailActionLedgerRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/briefs/$briefId/action-ledger',
  component: BriefDetailActionLedgerPage,
});
const briefDetailNotesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/briefs/$briefId/notes',
  component: BriefDetailNotesPage,
});
const briefDetailCitationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/briefs/$briefId/citations',
  component: BriefDetailCitationsPage,
});
const briefDetailOutcomesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/briefs/$briefId/outcomes',
  component: BriefDetailOutcomesPage,
});
const briefDetailSourceSnapshotRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/briefs/$briefId/source-snapshot',
  component: BriefDetailSourceSnapshotPage,
});
const briefDetailShareRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/briefs/$briefId/share',
  component: BriefDetailSharePage,
});
const flightPlanRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/approvals/flight-plan',
  beforeLoad: requireMembership,
  component: FlightPlanPage,
});

// Org admin
const adminSettingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/settings',
  beforeLoad: requireOrgAdmin,
  component: AdminSettingsPage,
});
const orgSettingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/org',
  beforeLoad: requireOrgAdmin,
  component: OrgSettingsPage,
});
// Unified team surface (v6 dev-handoff #114). `/settings/org/team` is
// the canonical URL; `/members` + `/invites` are legacy aliases that
// render the same component for backward-compat.
const orgTeamRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/org/team',
  beforeLoad: requireOrgAdmin,
  component: OrgTeamPage,
});
const orgMembersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/org/members',
  beforeLoad: requireOrgAdmin,
  component: OrgTeamPage,
});
const orgInvitesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings/org/invites',
  beforeLoad: requireOrgAdmin,
  component: OrgTeamPage,
});

// v6 dev-handoff (#114): /admin/billing (orgAdmin), /profile (authed),
// /empty (authed — design preview of the post-onboarding zero-data
// state), and unified /404 + /403 routes for design review. The
// router's catch-all also points at NotFoundPage.
const billingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/billing',
  beforeLoad: requireOrgAdmin,
  component: BillingPage,
});
const profileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/profile',
  component: ProfilePage,
});
// /profile/company — Trust-section orgMember entry (epic #123 Phase 3).
// The page is readable by every org member; admin-only edits are
// enforced inside the page (told rows render read-only for non-admins)
// + at the route layer on PUT calls.
const companyProfileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/profile/company',
  beforeLoad: requireMembership,
  component: CompanyProfilePage,
});
const emptyWorkspaceRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/empty',
  component: EmptyWorkspacePage,
});
/**
 * `/onboarding/checklist` — post-go-live "5 things to do this week"
 * surface. Mounted under authedRoute so it inherits the standard
 * portal chrome (sidebar + topbar + rail). Membership-gated — every
 * onboarded user gets the same list; admins + members both land here.
 */
const setupChecklistRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/onboarding/checklist',
  beforeLoad: requireMembership,
  component: SetupChecklistPage,
});
const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/404',
  component: NotFoundPage,
});
const permissionDeniedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/403',
  component: PermissionDeniedPage,
});

// v7 dev-handoff (Epic #123 Phase 6): read-only `/permissions` matrix.
// Admin-only at the router layer. Sidebar visibility is also gated on
// `useIsOrgAdmin()` so non-admins never see the entry.
const permissionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/permissions',
  beforeLoad: requireOrgAdmin,
  component: PermissionsPage,
});

// SuperAdmin
const superAdminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/superadmin',
  beforeLoad: requireSuperAdmin,
  component: SuperAdminPage,
});
const superAdminOrgsNewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/superadmin/orgs/new',
  beforeLoad: requireSuperAdmin,
  component: SuperAdminOrgsNewPage,
});
const superAdminUsersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/superadmin/users',
  beforeLoad: requireSuperAdmin,
  component: SuperAdminUsersPage,
});
const superAdminAuditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/superadmin/audit',
  beforeLoad: requireSuperAdmin,
  component: SuperAdminAuditPage,
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  signUpRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  verifyEmailRoute,
  verifyEmailSentRoute,
  mfaEnrollRoute,
  mfaChallengeRoute,
  inviteAcceptRoute,
  onboardingSelectOrgRoute,
  onboardingNoOrgRoute,
  onboardingCreateOrgRoute,
  onboardingWizardRoute,
  onboardingAgentLedRoute,
  onboardingWelcomeRoute,
  // Design-pass wave (May 2026) — post-wizard reveal page (full-bleed).
  onboardingRevealRoute,
  onboardingIngestionRoute,
  onboardingInviteTeamRoute,
  // Onboarding polish (May 2026) — full-bleed celebration + first-run
  // tour. /first-briefing-ready is the Day 2-3 magic moment; the
  // /onboarding/walkthrough overlay is the forced first-run tour. The
  // /onboarding/checklist surface lives below under authedRoute (it
  // wants the standard portal chrome).
  firstBriefingReadyRoute,
  onboardingWalkthroughRoute,
  // v6 dev-handoff (#114) — unauth-tolerant 404 + 403 surfaces.
  notFoundRoute,
  permissionDeniedRoute,
  authedRoute.addChildren([
    rootRedirectRoute,
    // Portal redesign nav stubs
    briefingRoute,
    todayRoute,
    // Brief-as-Dossier (#166 PR B) — archive list.
    briefsArchiveRoute,
    askRoute,
    dossiersRoute,
    insightsRoute,
    signalsRoute,
    externalSignalsRoute,
    alertsRoute,
    approvalsRoute,
    notificationsRoute,
    agentRehearsalRoute,
    // Agent trust loop (#166 PR A)
    agentQuestionsRoute,
    agentCalibrationRoute,
    agentChangelogRoute,
    agentCorrectionsRoute,
    agentFlightPlanRoute,
    agentSlackPreviewRoute,
    settingsAgentRoute,
    sourcesWeightsRoute,
    settingsCadenceRoute,
    dossierChatRoute,
    sourcesTrustRoute,
    adminAuditDiffRoute,
    metricDrillRoute,
    mobileCompanionRoute,
    // Governance pillar (v5)
    governanceRoute,
    governanceCalendarRoute,
    governanceHelpRoute,
    governanceBoardPackageRoute,
    governanceItemRoute,
    // v5 net-new screens
    orgChartRoute,
    insightDossierRoute,
    statesShowcaseRoute,
    walkthroughRoute,
    outboundPreviewRoute,
    agentRoute,
    sourcesRoute,
    bookmarksRoute,
    todosRoute,
    discoveryRoute,
    adminPeopleRoute,
    adminAuditRoute,
    adminEntityResolutionRoute,
    adminConnectorsRoute,
    // v6 Integrations directory (issue #114)
    adminIntegrationsRoute,
    adminIntegrationsGoogleWorkspaceRoute,
    adminIntegrationsGoogleWorkspaceConfigureRoute,
    adminIntegrationsGoogleWorkspaceHealthRoute,
    adminIntegrationsAuditRoute,
    adminIntegrationsPermissionsRoute,
    // Playbook
    playbookRoute,
    departmentPlaybookRoute,
    // Notes
    notesRoute,
    // Dossier detail
    dossierPersonRoute,
    dossierTopicRoute,
    // EOS surfaces (post-#143: /vto + /people-analyzer deleted)
    rocksRoute,
    scorecardRoute,
    issuesRoute,
    l10Route,
    accountabilityRoute,
    // v7 Phase 5 (#123) — rules & automations
    rulesRoute,
    // Design-pass wave (May 2026) — 7 new in-product routes
    memoryRoute,
    nearMissesRoute,
    decisionsRoute,
    notesTimelineRoute,
    searchRoute,
    briefingOvernightRoute,
    // Brief-as-Dossier detail routes (#166 PR B)
    briefDetailItemsRoute,
    briefDetailItemsAliasRoute,
    briefDetailActionLedgerRoute,
    briefDetailNotesRoute,
    briefDetailCitationsRoute,
    briefDetailOutcomesRoute,
    briefDetailSourceSnapshotRoute,
    briefDetailShareRoute,
    flightPlanRoute,
    // Org admin
    adminSettingsRoute,
    orgSettingsRoute,
    orgTeamRoute,
    orgMembersRoute,
    orgInvitesRoute,
    billingRoute,
    // v7 dev-handoff (Epic #123 Phase 6): /permissions
    permissionsRoute,
    // v6 dev-handoff (#114)
    profileRoute,
    // v7 redesign — epic #123 Phase 3
    companyProfileRoute,
    emptyWorkspaceRoute,
    // Onboarding polish (May 2026) — post-go-live setup checklist
    // surface (lives inside the portal chrome, unlike the full-bleed
    // /onboarding/welcome / /onboarding/walkthrough / /onboarding/reveal
    // / /first-briefing-ready siblings).
    setupChecklistRoute,
    // SuperAdmin
    superAdminRoute,
    superAdminOrgsNewRoute,
    superAdminUsersRoute,
    superAdminAuditRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // v6 dev-handoff (#114): unknown URLs render the unified NotFound
  // screen with `kind="404"`. The 403 variant is reached explicitly via
  // `/403` or through permission gates that opt in to it.
  defaultNotFoundComponent: NotFoundPage,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
